import { isNonEmpty } from '@sgme/fp';
import { tradeRequestApi } from 'apiConnector/quote/trade';
import { parseISO } from 'date-fns';
import { isQuoteEligibleForExecution } from 'epic/utils/pricing/quoteValidity';
import { quoteOrTabRemoved } from 'epic/workspace/common/tabs/tabsEpics/quoteOrTabRemoved';
import { ofType } from 'redux-observable';
import type { SchedulerLike } from 'rxjs';
import { EMPTY, merge, NEVER, of, race } from 'rxjs';
import {
  catchError,
  delay,
  filter,
  mergeMap,
  startWith,
  takeUntil,
  timeout,
  timeoutWith,
  withLatestFrom,
} from 'rxjs/operators';
import { commonQuoteAction } from 'state/forms/actions/quote';
import { commonTradeAction } from 'state/forms/actions/trade';
import { statifiedTradeAuthReceivedThunk, statifiedTradeAuthTimeoutThunk } from 'state/forms/thunks';
import type { NextTradeAuthorizationResult } from 'state/quotes/trades/trades.model';
import { isQuoteTypeTradable } from 'state/quotes/utils';
import type { EpicWithThunk } from 'state/state.common';
import { dateNow } from 'utils/core/now';
import { fatEventApiConnector } from 'utils/streaming/api';
import { tradeAuthorization } from 'utils/streaming/messages';

// TODO catch Error in Epic
export function statifiedTradeAuthorizationEpic(appScheduler?: SchedulerLike): EpicWithThunk {
  return (action$, state$, { oldHttpClient, getSgmeConfiguration, streaming }) =>
    action$.pipe(
      ofType('QUOTE_ACKED'),
      filter(
        ({ quoteType, workspace }) => ['static', 'essential'].includes(workspace) && isQuoteTypeTradable(quoteType),
      ),
      mergeMap(({ tabId, quoteId, workspace }) => {
        const quoteOrTabRemoved$ = quoteOrTabRemoved(action$, tabId, quoteId, state$);
        const tradeRequested$ = action$.pipe(
          ofType('QUOTE_TRADE_REQUESTED'),
          filter(action => ['static', 'essential'].includes(action.workspace) && action.tabId === tabId),
        );
        const tradeAuthTimeoutMs = getSgmeConfiguration().tradeAuthTimeoutMs;

        const tradeAuth$ = tradeAuthorization(streaming).pipe(
          filter(tradeAuth => tradeAuth.quoteId === quoteId),
          mergeMap(lightEvent =>
            fatEventApiConnector(oldHttpClient)
              .getFatEvent<NextTradeAuthorizationResult>(lightEvent)
              .pipe(catchError(_ => EMPTY)),
          ),
          takeUntil(quoteOrTabRemoved$),
          mergeMap(tradeAuth => {
            if (tradeAuth.status === 'Granted') {
              return of(commonQuoteAction.quoteStale(workspace, tabId, quoteId, false)).pipe(
                delay(tradeAuth.expirationDelayInSeconds * 1000, appScheduler),
                startWith(statifiedTradeAuthReceivedThunk(workspace, tabId, quoteId, tradeAuth)),
                takeUntil(merge(tradeRequested$, quoteOrTabRemoved$)),
              );
            } else {
              return of(statifiedTradeAuthReceivedThunk(workspace, tabId, quoteId, tradeAuth));
            }
          }),
        );

        const timeout$ = NEVER.pipe(
          timeoutWith(tradeAuthTimeoutMs, of(statifiedTradeAuthTimeoutThunk(workspace, tabId, quoteId)), appScheduler),
        );
        return race([tradeAuth$, timeout$]);
      }),
    );
}

export function statifiedPostReloadTradeAuthorizationEpic(appScheduler?: SchedulerLike): EpicWithThunk {
  return (action$, state$, { oldHttpClient, frontLogger, getSgmeConfiguration, getNow }) =>
    action$.pipe(
      ofType('PREVIOUS_QUOTE_LOADED'),
      withLatestFrom(state$),
      filter(
        ([{ quoteId, quote, workspace }, state]) =>
          ['static', 'essential'].includes(workspace) && isQuoteEligibleForExecution(state, quoteId, quote),
      ),
      mergeMap(([{ tabId, quoteId, workspace }]) => {
        const quoteOrTabRemoved$ = quoteOrTabRemoved(action$, tabId, quoteId, state$);
        const tradeAuthTimeoutMs = getSgmeConfiguration().tradeAuthTimeoutMs;

        const tradeAuth$ = tradeRequestApi(oldHttpClient, tradeAuthTimeoutMs)
          .getTradeAuth(quoteId)
          .pipe(
            takeUntil(quoteOrTabRemoved$),
            mergeMap(tradeAuth => {
              if (tradeAuth.status !== 'Granted') {
                return of(statifiedTradeAuthReceivedThunk(workspace, tabId, quoteId, tradeAuth));
              }
              let remainingDelay: number = tradeAuth.expirationDelayInSeconds * 1000;
              if (isNonEmpty(tradeAuth.expiresOn)) {
                const tradeAuthExpirationTime = parseISO(tradeAuth.expiresOn).getTime();
                const now = dateNow();
                if (tradeAuthExpirationTime > now) {
                  remainingDelay = tradeAuthExpirationTime - now;
                } else {
                  remainingDelay = now - tradeAuthExpirationTime;
                }
              }

              return of(commonQuoteAction.quoteStale(workspace, tabId, quoteId, false)).pipe(
                delay(remainingDelay, appScheduler),
                startWith(statifiedTradeAuthReceivedThunk(workspace, tabId, quoteId, tradeAuth)),
                takeUntil(quoteOrTabRemoved$),
              );
            }),
            catchError((error: Error) => {
              frontLogger.error(
                'An error occurred inside statifiedTradeAuthorizationEpic requesting Trade Auth: {@FrontError}',
                error,
              );
              return of(statifiedTradeAuthTimeoutThunk(workspace, tabId, quoteId));
            }),
          );

        const tradeExec$ = tradeRequestApi(oldHttpClient, tradeAuthTimeoutMs)
          .getTradeExec(quoteId)
          .pipe(
            takeUntil(quoteOrTabRemoved$),
            mergeMap(tradeExec => {
              const now = getNow();
              if (tradeExec.status !== 'FULLY_EXECUTED') {
                return of(commonTradeAction.quoteTradeRejected(workspace, tabId, quoteId, tradeExec.errors ?? [], now));
              }
              return of(commonQuoteAction.quoteStale(workspace, tabId, quoteId, false)).pipe(
                takeUntil(quoteOrTabRemoved$),
              );
            }),
            catchError((error: Error) => {
              frontLogger.error(
                'An error occurred inside statifiedTradeAuthorizationEpic requesting Trade Exec: {@FrontError}',
                error,
              );
              return EMPTY;
            }),
          );

        const timeout$ = NEVER.pipe(
          timeout({
            each: tradeAuthTimeoutMs,
            with: () => of(statifiedTradeAuthTimeoutThunk(workspace, tabId, quoteId)),
            scheduler: appScheduler,
          }),
        );
        return race([tradeAuth$, tradeExec$, timeout$]);
      }),
    );
}
